import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { ApiClientError } from 'api/client';
import { useToast } from 'app/hooks/useToast';
import { CognitoTokenManager } from 'app/modules/auth/utils/cognitoTokenManager';
import { CognitoApi } from 'app/modules/auth/api/cognitoApi';
import { AccountSwitcherApi } from '../api/accountSwitcherApi';
import config from 'config';
import routeConfig from 'app/config/routes';
import Logger from 'utils/logger';
import { getBasePath } from 'utils/url';

const getAccountUrl = (accountName: string): string => {
  const baseDomain =
    config.MODE === 'production' ? 'kontentino.com' : 'dev-ec1.kontentino.dev';
  return `https://${accountName}.${baseDomain}`;
};

export const useSwitchAccountMutation = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation(
    (accountId: number) => AccountSwitcherApi.switchAccount(accountId),
    {
      onSuccess: async (response) => {
        const { account } = response;

        try {
          const tokens = CognitoTokenManager.getTokens();

          if (!tokens) {
            throw new Error('No tokens available for session transfer');
          }

          const targetOrigin = getAccountUrl(account.account_name);

          const { session_token } = await CognitoApi.createSessionToken({
            id_token: tokens.idToken,
            access_token: tokens.accessToken,
            refresh_token: tokens.refreshToken,
            account_id: account.account_id,
            origin: targetOrigin,
          });

          queryClient.clear();

          const basePath = getBasePath();

          window.location.href = `${targetOrigin}${basePath}/${
            routeConfig.authCallback.path
          }?session_token=${encodeURIComponent(session_token)}`;
        } catch (err) {
          Logger.error(
            'Failed to create session token for account switch',
            (err as Error)?.message,
          );
          toast(t('somethingWentWrong'), 'error');
        }
      },
      onError: (error: ApiClientError) => {
        toast(error?.userMessage ?? t('somethingWentWrong'), 'error');
      },
    },
  );
};
